import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  OperationPart,
  OperationPartItem,
  BusinessSpecifities,
  OperationLaborTimeItem,
  LaborTime,
} from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';

import { LaborPriceItem } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetSettings';
import { RootState } from 'app/AppStore';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import {
  getEasyBasketSelectedLaborTimesMap,
  getEasyBasketSelectedOperationId,
  getEasyBasketSelectedReferencesMap,
  getSelectedLaborTimes,
  getEasyBasketOperationDetails,
} from 'domains/easyBasket/EasyBasket.store';
import { getEstimateSettings } from 'domains/estimate/Estimate.store';
import { MECHANICS } from 'domains/estimate/Estimate.types';
import { useFetchPrices, useFetchReferencesStocks } from 'domains/references/References.requests';
import { getTechnicityLevel } from 'pages/CatalogPage/DH/SubcategorySection/LaborTimeSection/utils';
import { getData, NO_DATA, NOT_FOUND } from 'utils';

export const FIRST = 'FIRST';
export const MIDDLE = 'MIDDLE';
export const LAST = 'LAST';
export const UNKNOWN = 'UNKNOWN';

export type PartPosition = typeof FIRST | typeof MIDDLE | typeof LAST | typeof UNKNOWN;

export const getPartPosition = (index: number, itemsCount: number | undefined): PartPosition => {
  if (!itemsCount) {
    return UNKNOWN;
  }

  switch (index) {
    case 0:
      return FIRST;
    case itemsCount - 1:
      return LAST;
    default:
      return MIDDLE;
  }
};

export const parseLaborTimeDesignation = (designation: string): string => {
  const colonIndex = designation.indexOf(':');
  if (colonIndex === -1) {
    return designation.trim();
  }
  return designation.substring(colonIndex + 1).trim();
};

export const cleanDuplicatedPartsInGroups = (partItems?: OperationPartItem[]): OperationPartItem[] | undefined => {
  if (!partItems) {
    return;
  }

  return partItems.map((item) => {
    const seen = new Map<string, OperationPart>();
    const result: OperationPart[] = [];

    for (const part of item.parts) {
      const key = `${part.reference}|${part.quantity ?? ''}`;
      if (!seen.has(key)) {
        seen.set(key, part);
        result.push(part);
      }
    }
    return {
      ...item,
      parts: result,
    };
  });
};

export const ON = 'on';

export const mergeSingleReferenceDuplicatedGroups = (
  partItems?: OperationPartItem[],
): OperationPartItem[] | undefined => {
  if (!partItems) {
    return;
  }

  const singlePartGroupsOn = partItems.filter((i) => i.parts.length === 1 && i.defaultValue === ON);

  const singlePartGroupsOthers = partItems.filter((i) => i.parts.length === 1 && i.defaultValue !== ON);

  const multiPartGroups = partItems.filter((i) => i.parts.length > 1);

  const merged = Array.from(
    singlePartGroupsOn
      .reduce((map, item) => {
        const part = item.parts[0];
        const ref = part.reference;
        const qty = Number(part.quantity ?? 0);

        const existing = map.get(ref);

        if (!existing) {
          map.set(ref, { ...item, parts: [{ ...part, quantity: String(qty) }] });
        } else {
          const existingPart = existing.parts[0];
          existingPart.quantity = String(Number(existingPart.quantity ?? 0) + qty);
        }

        return map;
      }, new Map<string, OperationPartItem>())
      .values(),
  );

  return [...merged, ...singlePartGroupsOthers, ...multiPartGroups];
};

const findRateForKey = (laborTimeRates: LaborPriceItem[], key: string, technicityType: string): string | undefined => {
  const technicityLevel = getTechnicityLevel(technicityType);
  return (
    laborTimeRates.find((rate) => rate.technicityType === key.toUpperCase() && rate.technicityLevel === technicityLevel)
      ?.price ??
    laborTimeRates.find((rate) => rate.technicityType === MECHANICS && rate.technicityLevel === technicityLevel)?.price
  );
};

const hasAnyPrice = (
  businessSpecificities: BusinessSpecifities | undefined,
  technicityType: string | undefined,
  laborTimeRates: LaborPriceItem[] | undefined,
): boolean => {
  if (!businessSpecificities || !technicityType || !laborTimeRates) {
    return false;
  }
  return Object.entries(businessSpecificities).some(([key, value]) => {
    const numericValue = Number.parseFloat(value.replace(',', '.'));
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      return false;
    }
    return !!findRateForKey(laborTimeRates, key, technicityType);
  });
};

const getLaborRateForTechnicity = (laborTimeRates: LaborPriceItem[], key: string, technicityType: string): string => {
  return findRateForKey(laborTimeRates, key, technicityType) ?? '0';
};

export const useCalculateLaborTimePrices = (
  businessSpecificities: BusinessSpecifities | undefined,
  technicityType: string | undefined,
): { vatInclPrice: string; vatExclPrice: string } | NO_DATA => {
  const estimateSettings = useSelector(getEstimateSettings);
  const estimateSettingsData = getData(estimateSettings);
  const laborTimeRates = estimateSettingsData?.laborPriceList;
  const vatRate = estimateSettingsData?.vatRate;
  const isAnyPrice = hasAnyPrice(businessSpecificities, technicityType, laborTimeRates);

  if (
    !businessSpecificities ||
    !laborTimeRates ||
    !vatRate ||
    !technicityType ||
    !getTechnicityLevel(technicityType) ||
    !isAnyPrice
  ) {
    return NOT_FOUND;
  }
  const computedTotalPrice = Object.entries(businessSpecificities).reduce((total, [key]) => {
    const time = businessSpecificities[key as keyof BusinessSpecifities];
    const settingsRate = getLaborRateForTechnicity(laborTimeRates, key, technicityType);
    const normalizedTime = Number.parseFloat(time.replace(',', '.'));
    const normalizedRate = Number.parseFloat(settingsRate.replace(',', '.'));
    const priceForThisType = normalizedTime * normalizedRate;
    return total + priceForThisType;
  }, 0);

  const totalWithVat = computedTotalPrice * (1 + vatRate / 100);

  const vatExclPrice = computedTotalPrice.toFixed(2);
  const vatInclPrice = totalWithVat.toFixed(2);

  return { vatInclPrice, vatExclPrice };
};

export const useTotalReferencesPrice = (): { totalVatIncluded: number; totalVatExcluded: number } => {
  const selectedReferences = useSelector(getEasyBasketSelectedReferencesMap);

  const { totalVatIncluded, totalVatExcluded } = useMemo(() => {
    const refs = Array.from(selectedReferences.values());
    return refs.reduce(
      (acc, reference) => {
        const qty = reference?.quantity ?? 1;
        const vatIncl = Number.parseFloat(reference?.price?.priceVatIncluded ?? '0') || 0;
        const vatExcl = Number.parseFloat(reference?.price?.priceVatExcluded ?? '0') || 0;
        acc.totalVatIncluded += vatIncl * qty;
        acc.totalVatExcluded += vatExcl * qty;
        return acc;
      },
      { totalVatIncluded: 0, totalVatExcluded: 0 },
    );
  }, [selectedReferences]);

  return { totalVatIncluded, totalVatExcluded };
};

export const useTotalLaborTimePrice = (): { totalVatIncluded: number; totalVatExcluded: number } => {
  const selectedLaborTimes = useSelector(getEasyBasketSelectedLaborTimesMap);
  const { totalVatIncluded, totalVatExcluded } = useMemo(() => {
    const laborTimes = Array.from(selectedLaborTimes.values());
    return laborTimes.reduce(
      (acc, laborTime) => {
        const vatIncl = Number.parseFloat(laborTime?.price?.priceVatIncluded ?? '0') || 0;
        const vatExcl = Number.parseFloat(laborTime?.price?.priceVatExcluded ?? '0') || 0;
        acc.totalVatIncluded += vatIncl;
        acc.totalVatExcluded += vatExcl;
        return acc;
      },
      { totalVatIncluded: 0, totalVatExcluded: 0 },
    );
  }, [selectedLaborTimes]);

  return { totalVatIncluded, totalVatExcluded };
};

export const useTotalPrice = (): { totalVatIncluded: number; totalVatExcluded: number } => {
  const referencesTotal = useTotalReferencesPrice();
  const laborTimesTotal = useTotalLaborTimePrice();

  return {
    totalVatIncluded: referencesTotal.totalVatIncluded + laborTimesTotal.totalVatIncluded,
    totalVatExcluded: referencesTotal.totalVatExcluded + laborTimesTotal.totalVatExcluded,
  };
};

export const parseDuration = (d?: string): number => {
  if (!d) {
    return 0;
  }
  const n = Number.parseFloat(d.replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
};

// main GO is the GO (operationType 1) with the longest duration among all labor time groups
export const useGlobalMainGOLaborTimeId = (
  laborTimesGroups: OperationLaborTimeItem[] | undefined,
): string | undefined => {
  return useMemo(() => {
    if (!laborTimesGroups?.length) {
      return undefined;
    }
    let best: LaborTime | undefined;
    let bestDur = -1;

    const updateBest = (lt: LaborTime) => {
      const dur = parseDuration(lt.duration);
      if (dur > bestDur) {
        bestDur = dur;
        best = lt;
      }
    };

    for (const group of laborTimesGroups) {
      for (const lt of group.laborTimes) {
        if (lt.operationType === '1') {
          updateBest(lt);
        }
      }
    }
    return best?.id;
  }, [laborTimesGroups]);
};

export const useSortLaborTimeGroups = (
  laborTimesGroups: OperationLaborTimeItem[] | undefined,
): OperationLaborTimeItem[] => {
  return useMemo(() => {
    if (!laborTimesGroups?.length) {
      return [];
    }

    let bestGroup: OperationLaborTimeItem | undefined;
    let bestMaxType1 = -1;

    for (const g of laborTimesGroups) {
      const maxType1 = g.laborTimes
        .filter((lt) => lt.operationType === '1')
        .reduce((m, lt) => {
          const dur = parseDuration(lt.duration);
          return dur > m ? dur : m;
        }, -1);

      if (maxType1 > bestMaxType1) {
        bestMaxType1 = maxType1;
        bestGroup = g;
      }
    }

    const rest = bestGroup ? laborTimesGroups.filter((g) => g !== bestGroup) : laborTimesGroups;

    rest.sort((a, b) => {
      const maxA = a.laborTimes.reduce((m, lt) => Math.max(m, parseDuration(lt.duration)), 0);
      const maxB = b.laborTimes.reduce((m, lt) => Math.max(m, parseDuration(lt.duration)), 0);
      return maxB - maxA;
    });

    return bestGroup ? [bestGroup, ...rest] : rest;
  }, [laborTimesGroups]);
};

export const useSortLaborTimes = (laborTimes: LaborTime[] | undefined): LaborTime[] =>
  useMemo(() => {
    if (!laborTimes) {
      return [];
    }
    if (laborTimes.length <= 1) {
      return laborTimes;
    }

    return [...laborTimes].sort((a, b) => {
      const diff = parseDuration(b.duration) - parseDuration(a.duration);
      if (diff !== 0) {
        return diff;
      }
      if (a.operationType === b.operationType) {
        return 0;
      }
      const aIsType1 = a.operationType === '1';
      const bIsType1 = b.operationType === '1';
      if (aIsType1 && !bIsType1) {
        return -1;
      }
      if (!aIsType1 && bIsType1) {
        return 1;
      }
      return 0;
    });
  }, [laborTimes]);

export const getHasMultipleIdenticalLaborTimes = (laborTimes?: LaborTime[]): boolean => {
  const original = laborTimes?.map((lt) => `${lt.element}|${lt.operation}|${lt.operationType}`) || [];
  const unique = Array.from(new Set(original));
  return unique.length < original.length;
};

export const useIsAnyGoSelected = (): boolean => {
  const selectedLaborTimes = useSelector(getSelectedLaborTimes);
  const allLts = Array.from(selectedLaborTimes.values());
  return allLts.some((lt) => lt.operationType === '1');
};

// Selection is valid if at least one GO is selected and all labor time groups with selectable items have a selection
export const useIsLaborTimeSelectionValid = (): boolean => {
  const isAnyGoSelected = useIsAnyGoSelected();
  const selectedLaborTimes = useSelector(getSelectedLaborTimes);
  const selectedOperationId = useSelector(getEasyBasketSelectedOperationId);
  const operationDetails = useSelector((state: RootState) => getEasyBasketOperationDetails(state, selectedOperationId));
  const laborTimeGroups = operationDetails?.data?.basketContent.laborTimeSection.laborTimeItems ?? [];
  const selectableGroups = laborTimeGroups.filter((g) => (g.laborTimes?.length ?? 0) > 0);
  const everyGroupHasSelection = selectableGroups.every((g) => selectedLaborTimes.has(g.itemGroup));
  return isAnyGoSelected && everyGroupHasSelection && selectableGroups.length > 0;
};

export const useGetCategoriziedReferences = (
  partsItems: OperationPartItem[] | undefined,
): OperationPartItem[] | undefined => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);

  const filteredDuplicatedGroupParts = cleanDuplicatedPartsInGroups(partsItems);
  const mergedDuplicatedGroups = mergeSingleReferenceDuplicatedGroups(filteredDuplicatedGroupParts);

  const allReferences = [
    ...new Set(
      partsItems?.flatMap((partItem) =>
        partItem.parts.flatMap((part) => [
          part.reference,
          ...(part.alternativeParts?.map((alt) => alt.reference) ?? []),
        ]),
      ) ?? [],
    ),
  ];

  useFetchPrices(allReferences, vehicleKey);
  useFetchReferencesStocks(allReferences);

  return mergedDuplicatedGroups;
};
